/**
 * LiveZoneSection - Live Zone configuration with toggle and content type pills
 * Design: Toggle cards with visual position selector
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';

const POSITIONS = [
    { id: 'top', label: 'Top' },
    { id: 'sidebar', label: 'Sidebar' },
    { id: 'bottom', label: 'Bottom' },
];

const CONTENT_TYPES = [
    { id: 'scores', icon: '🏆', label: 'Scores' },
    { id: 'standings', icon: '📊', label: 'Standings' },
    { id: 'news', icon: '📰', label: 'News' },
    { id: 'stats', icon: '📈', label: 'Stats' },
    { id: 'schedule', icon: '📅', label: 'Schedule' },
];

export default function LiveZoneSection({ preferences, onPreferenceChange, disabled }) {
    const [enabled, setEnabled] = useState(preferences?.liveZone?.enabled ?? true);
    const [position, setPosition] = useState(preferences?.liveZone?.position || 'sidebar');
    const [contentTypes, setContentTypes] = useState(
        preferences?.liveZone?.contentTypes || ['scores', 'news']
    );
    const [saving, setSaving] = useState(false);

    // Sync with props
    useEffect(() => {
        if (preferences?.liveZone) {
            setEnabled(preferences.liveZone.enabled ?? true);
            setPosition(preferences.liveZone.position || 'sidebar');
            setContentTypes(preferences.liveZone.contentTypes || ['scores', 'news']);
        }
    }, [preferences]);

    const savePreference = async (updates) => {
        setSaving(true);
        try {
            await axios.patch('/q/api/user-preferences', { liveZone: updates });
            onPreferenceChange?.({ type: 'success', text: 'Live Zone updated' });
        } catch (error) {
            onPreferenceChange?.({ type: 'error', text: 'Failed to save Live Zone settings' });
        } finally {
            setSaving(false);
        }
    };

    const handleToggle = () => {
        const newEnabled = !enabled;
        setEnabled(newEnabled);
        savePreference({ enabled: newEnabled });
    };

    const handlePositionChange = (pos) => {
        setPosition(pos);
        savePreference({ position: pos });
    };

    const handleContentTypeToggle = (typeId) => {
        const newTypes = contentTypes.includes(typeId)
            ? contentTypes.filter(t => t !== typeId)
            : [...contentTypes, typeId];
        setContentTypes(newTypes);
        savePreference({ contentTypes: newTypes });
    };

    return (
        <section id="livezone" className="settings-section">
            <div className="settings-section-header">
                <span className="settings-section-icon">📡</span>
                <h2 className="settings-section-title">Live Zone</h2>
            </div>
            <p className="settings-section-description">
                Configure real-time sports updates including scores, standings, and breaking news.
            </p>

            {/* Enable/Disable Toggle */}
            <div className="livezone-header">
                <div className="livezone-toggle">
                    <span className="livezone-toggle-label">Enable Live Zone</span>
                    <button
                        className={`toggle-switch ${enabled ? 'active' : ''}`}
                        onClick={handleToggle}
                        disabled={disabled || saving}
                        role="switch"
                        aria-checked={enabled}
                        aria-label="Enable Live Zone"
                    >
                        <span className="toggle-switch-handle"></span>
                    </button>
                </div>
            </div>

            {/* Content that's disabled when Live Zone is off */}
            <div className={!enabled ? 'livezone-content-disabled' : ''}>
                {/* Position Selector */}
                <div className="appearance-subsection">
                    <h4 className="appearance-subsection-title">Position</h4>
                    <div className="position-selector" role="radiogroup" aria-label="Live Zone position">
                        {POSITIONS.map((pos) => (
                            <button
                                key={pos.id}
                                className={`position-option ${position === pos.id ? 'selected' : ''}`}
                                onClick={() => handlePositionChange(pos.id)}
                                disabled={disabled || saving || !enabled}
                                role="radio"
                                aria-checked={position === pos.id}
                            >
                                {pos.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Content Types */}
                <div className="appearance-subsection">
                    <h4 className="content-type-title">Content Types</h4>
                    <div className="content-type-pills" role="group" aria-label="Live Zone content types">
                        {CONTENT_TYPES.map((type) => (
                            <button
                                key={type.id}
                                className={`content-type-pill ${contentTypes.includes(type.id) ? 'selected' : ''}`}
                                onClick={() => handleContentTypeToggle(type.id)}
                                disabled={disabled || saving || !enabled}
                                aria-pressed={contentTypes.includes(type.id)}
                            >
                                <span className="content-type-pill-icon">{type.icon}</span>
                                <span>{type.label}</span>
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
